import { initializePermissionApi, PermissionApi } from '@data-delivery/permission-ui-api';
import { useMutation } from '@tanstack/react-query';
import { permissionQueryClient } from '../../query-client';
import { businessGroupsKeys } from './business-groups-keys';

const businessGroupsApi = initializePermissionApi(PermissionApi.BusinessGroupsApi);

export function useDeleteBusinessGroupMutation() {
  return useMutation<
    PermissionApi.CascadeDeleteSummaryDto,
    unknown,
    string,
    { prev?: PermissionApi.BusinessGroupEntity[] }
  >({
    mutationKey: ['business-groups', 'delete'],
    async mutationFn(id: string) {
      const res = await businessGroupsApi.deleteBusinessGroupCascade({ id });
      return res.data;
    },
    async onMutate(id: string) {
      await permissionQueryClient.cancelQueries({ queryKey: businessGroupsKeys.list() });
      const prev = permissionQueryClient.getQueryData<PermissionApi.BusinessGroupEntity[]>(
        businessGroupsKeys.list(),
      );
      if (prev) {
        permissionQueryClient.setQueryData(
          businessGroupsKeys.list(),
          prev.filter(bg => bg.id !== id),
        );
      }

      return { prev } as const;
    },
    onError(_err, _id, ctx) {
      if (ctx?.prev) {
        permissionQueryClient.setQueryData(businessGroupsKeys.list(), ctx.prev);
      }
    },
    onSuccess(_summary) {
      permissionQueryClient.invalidateQueries({ queryKey: businessGroupsKeys.list() });
    },
  });
}

export function useCreateBusinessGroupMutation() {
  return useMutation({
    mutationKey: ['business-groups', 'create'],
    async mutationFn(dto: PermissionApi.CreateBusinessGroupDto) {
      const res = await businessGroupsApi.createBusinessGroup({ createBusinessGroupDto: dto });
      return res.data;
    },
    async onMutate(dto: PermissionApi.CreateBusinessGroupDto) {
      await permissionQueryClient.cancelQueries({ queryKey: businessGroupsKeys.list() });
      const prev = permissionQueryClient.getQueryData<PermissionApi.BusinessGroupEntity[]>(
        businessGroupsKeys.list(),
      );
      const id = dto.shortName.toLowerCase().replace(/\s+/g, '-');
      const optimistic: PermissionApi.BusinessGroupEntity = {
        id,
        shortName: dto.shortName,
        name: dto.name,
        description: dto.description,
        services: dto.services as PermissionApi.BusinessGroupEntityServicesEnum[],
        contactComms: dto.contactComms,
        contactDevelopment: dto.contactDevelopment,
        contactProduct: dto.contactProduct,
        contactSupport: dto.contactSupport,
        loginAsAdmins: [],
        lastModified: Date.now(),
        lastModifiedBy: 'optimistic-user',
      };
      permissionQueryClient.setQueryData(businessGroupsKeys.list(), [...(prev ?? []), optimistic]);
      return { prev, optimistic } as const;
    },
    onError(_err, _dto, ctx) {
      if (ctx?.prev) {
        permissionQueryClient.setQueryData(businessGroupsKeys.list(), ctx.prev);
      }
    },
    onSuccess() {
      permissionQueryClient.invalidateQueries({ queryKey: businessGroupsKeys.list() });
    },
  });
}

export function useUpdateBusinessGroupMutation() {
  return useMutation({
    mutationKey: ['business-groups', 'update'],
    async mutationFn(vars: { id: string; dto: PermissionApi.UpdateBusinessGroupDto }) {
      const res = await businessGroupsApi.updateBusinessGroup({
        id: vars.id,
        updateBusinessGroupDto: vars.dto,
      });
      return res.data;
    },
    async onMutate({ id, dto }: { id: string; dto: PermissionApi.UpdateBusinessGroupDto }) {
      await permissionQueryClient.cancelQueries({ queryKey: businessGroupsKeys.list() });
      const prev = permissionQueryClient.getQueryData<PermissionApi.BusinessGroupEntity[]>(
        businessGroupsKeys.list(),
      );
      if (prev) {
        const updated = prev.map(bg =>
          bg.id === id
            ? {
                ...bg,
                description: dto.description ?? bg.description,
                services:
                  (dto.services as PermissionApi.BusinessGroupEntityServicesEnum[]) ?? bg.services,
                contactComms: dto.contactComms ?? bg.contactComms,
                contactDevelopment: dto.contactDevelopment ?? bg.contactDevelopment,
                contactProduct: dto.contactProduct ?? bg.contactProduct,
                contactSupport: dto.contactSupport ?? bg.contactSupport,
                lastModified: dto.lastModified ?? bg.lastModified,
                lastModifiedBy: dto.lastModifiedBy ?? bg.lastModifiedBy,
              }
            : bg,
        );
        permissionQueryClient.setQueryData(businessGroupsKeys.list(), updated);
      }

      return { prev } as const;
    },
    onError(_err, _vars, ctx) {
      if (ctx?.prev) {
        permissionQueryClient.setQueryData(businessGroupsKeys.list(), ctx.prev);
      }
    },
    onSuccess(_data, vars) {
      permissionQueryClient.invalidateQueries({ queryKey: businessGroupsKeys.list() });
      permissionQueryClient.invalidateQueries({ queryKey: businessGroupsKeys.byId(vars.id) });
    },
  });
}

export function usePatchLoginAsAdminsMutation() {
  return useMutation({
    async mutationFn(vars: {
      businessGroupId: string;
      addedLoginAsAdmin: string[];
      removedLoginAsAdmin: string[];
    }) {
      const res = await businessGroupsApi.patchLoginAsAdminsForBusinessGroup({
        businessGroupId: vars.businessGroupId,
        patchLoginAsAdminsDto: {
          addedLoginAsAdmin: vars.addedLoginAsAdmin,
          removedLoginAsAdmin: vars.removedLoginAsAdmin,
        },
      });
      return res.data;
    },
    onSuccess(_data, vars) {
      permissionQueryClient.invalidateQueries({ queryKey: businessGroupsKeys.list() });
      permissionQueryClient.invalidateQueries({
        queryKey: businessGroupsKeys.byId(vars.businessGroupId),
      });
    },
  });
}
